import * as Yup from "yup";

import {
  BusinessPicker,
  InsurerPicker,
  OfficeButton,
  Paginator,
  ReInsurerPicker,
  SelectInput,
  Show,
  TableComponent,
  TextInput,
} from "components";
import {
  GenerateProductionReport,
  GenerateProductionReportVariables,
} from "_graphql/kek/mutation/__generated__/GenerateProductionReport";
import { facultativeReportOptions, wrapClick } from "utils";
import { insurerTable, reinsurerTable } from "./options";

import { Payment_status } from "_graphql/__generated__/globalTypes-kek";
import { exportToExcel } from "utils/excel";
import { generateKEKProductionReport } from "_graphql/kek/mutation/index.kek";
import moment from "moment";
import numeral from "numeral";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useLocalTableData } from "hooks/ui/use-local-table-data";
import { useMutation } from "@apollo/client";
import { usePagination } from "hooks";

const initialValues = {
  report_type: "",
  business_id: "",
  client_type: "",
  insurer_id: "",
  reinsurer_id: "",
  offer_status: [],
  payment_status: [],
  from: "",
  to: "",
};

const validationSchema = Yup.object().shape({
  business_id: Yup.string().required("Class of business is required"),
  client_type: Yup.string().required("Client type is required"),
  report_type: Yup.string().required("Report type is required"),
  insurer_id: Yup.string().when("client_type", {
    is: (val: string) => val === "Insurer",
    then: (schema) => schema.required("Insurer is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  reinsurer_id: Yup.string().when("client_type", {
    is: (val: string) => val === "Reinsurer",
    then: (schema) => schema.required("Reinsurer is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  from: Yup.string().required("From date is required"),
  to: Yup.string().required("To date is required"),
  offer_status: Yup.array()
    .min(1, "Select offer status")
    .required("Offer status is required"),
  payment_status: Yup.array().when("offer_status", {
    is: (val: string[]) => val.includes("CLOSED"),
    then: (schema) =>
      schema
        .min(1, "Select payment status")
        .required("Payment status is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const ProductionReport = () => {
  // const { showBanner } = useBatchReportContext();
  const { limit, offset, setPage, search } = usePagination();
  const [generate, { loading, data }] = useMutation<
    GenerateProductionReport,
    GenerateProductionReportVariables
  >(generateKEKProductionReport);
  const form = useFormik<any>({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      generate({
        variables: {
          reportData: {
            report_type: values.report_type,
            payment_status: values.offer_status.includes("CLOSED")
              ? values.payment_status
              : [],
            class_of_business_id: values.business_id,
            client_type: values.client_type,
            client_id:
              values.client_type === "INSURER"
                ? values.insurer_id
                : values.reinsurer_id,
            offer_status: values.offer_status,
            period_from: values.from,
            period_to: values.to,
          },
        },
      })
        .then(({ data }) => {})
        .catch((err) => {});
    },
  });

  const {
    paginatedData,
    total,
    data: allData,
  } = useLocalTableData({
    data: JSON.parse(data?.generateProductionReport || "[]") || [],
    limit: limit,
    offset: offset,
    search,
    parser: parseReportData,
  });

  useEffect(() => {
    if (form.values.report_type === "CLOSED") {
      form.setFieldValue("offer_status", ["CLOSED"]);
    }
  }, [form.values.report_type]);

  return (
    <div className="w-full">
      <div className="py-3 flex justify-between">
        <div>
          <h1 className="font-bold text-primary text-lg">Production Reports</h1>
        </div>
        <div className="flex items-center space-x-2">
          <OfficeButton
            disabled={loading}
            onClick={wrapClick(form.handleSubmit)}
          >
            {loading ? "Generating ..." : "Generate Report"}
          </OfficeButton>
          {paginatedData.length > 0 && (
            <button
              onClick={() =>
                exportToExcel({
                  data: allData,
                  fileName: `KEK_Production_Report_${new Date().toLocaleDateString()}`,
                  columns: getDataByType(form.values.client_type),
                })
              }
              className="bg-teal-500 text-white p-2 rounded-md shadow-sm"
            >
              Download Excel Report
            </button>
          )}
        </div>
      </div>
      <form className="space-y-6 divide-y shadow-sm rounded-md bg-white p-6 divide-gray-200">
        <div>
          <div className="grid grid-cols-2 gap-5">
            <div className="col-span-2">
              <SelectInput
                required
                id={"report_type"}
                label={"Report Type"}
                options={facultativeReportOptions}
                {...form}
              />
            </div>
            <div>
              <BusinessPicker
                required
                allValue="All"
                addAllOption
                filter={form.values.business_category}
                rawId
                id={"business_id"}
                placeholder="select class of business"
                label={"Class of Business"}
                {...form}
              />
            </div>
            <div>
              <SelectInput
                required
                id={"client_type"}
                label={"Client Type"}
                options={[
                  { label: "Reinsurer", value: "REINSURER" },
                  { label: "Insurer", value: "INSURER" },
                  {
                    label: "Retrocedent",
                    value: "RETROCEDENT",
                  },
                ]}
                {...form}
              />
            </div>

            {!["REINSURER", "RETROCEDENT"].includes(form.values.client_type) ? (
              <div className="">
                <InsurerPicker
                  allValue="All"
                  addAllOption
                  id={"insurer_id"}
                  rawId
                  label={"Select Insurer"}
                  placeholder="Select Insurer"
                  {...form}
                />
              </div>
            ) : (
              <div className="">
                <ReInsurerPicker
                  allValue="All"
                  addAllOption
                  rawId
                  id={"reinsurer_id"}
                  placeholder="Select Reinsurer"
                  label={"Select Reinsurer"}
                  {...form}
                />
              </div>
            )}
            <div>
              <SelectInput
                id="offer_status"
                label="Filter by offer status"
                disabled={form.values.report_type === "CLOSED"}
                multiple
                options={getOfferStatuses(form.values.offer_status)}
                required={true}
                {...form}
              />
            </div>
            <Show if={form.values.offer_status.includes("CLOSED")}>
              <div className="col-span-2">
                <SelectInput
                  id="payment_status"
                  label="Filter by payment status"
                  multiple
                  options={[
                    { label: "PAID", value: Payment_status.PAID },
                    { label: "PARTPAYMENT", value: Payment_status.PARTPAYMENT },
                    { label: "UNPAID", value: Payment_status.UNPAID },
                  ]}
                  required={true}
                  {...form}
                />
              </div>
            </Show>
          </div>
        </div>

        <div className="pt-6">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <TextInput
                id="from"
                label={"Period from"}
                type="date"
                placeholder=""
                required={true}
                {...form}
              />
            </div>
            <div>
              <TextInput
                id="to"
                label={"Period to"}
                type="date"
                placeholder=""
                required={true}
                {...form}
              />
            </div>
          </div>
        </div>
      </form>
      <Show
        if={
          Boolean(data) &&
          Boolean(
            JSON.parse(data?.generateProductionReport || "{}")?.can_queue_report
          )
        }
      >
        <div className="flex flex-col items-center justify-center py-8 bg-teal-400 rounded-md shadow-md mt-11">
          <svg
            className="w-16 h-16 text-primary-500 mb-4"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 12l2.5 2.5L16 9"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
          <h2 className="text-lg font-semibold text-primary-700 mb-2">
            Large datasets are optimized for background jobs
          </h2>
          <p className="text-sm text-gray-600 text-center max-w-xs">
            Your report involves a very large dataset, so we've queued it to
            process in the background. You'll find the completed report in the{" "}
            <span className="font-medium text-primary-600">
              Batched Reports{" "}
            </span>{" "}
            tab once it's ready.
          </p>
        </div>
      </Show>{" "}
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            loading={loading}
            title={"reports"}
            hasSearch
            columns={getDataByType(form.values.client_type)}
            data={paginatedData}
            refetch={form.handleSubmit}
            pagination={() => (
              <Paginator
                currentSize={paginatedData?.length}
                limit={limit}
                offset={offset}
                totalItems={total}
                setOffset={setPage}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductionReport;

const getOfferStatuses = (selected: string[]) => {
  // if the selected array contains all, don't show the rest
  if (selected.includes("ALL")) {
    return [{ label: "ALL", value: "ALL" }];
  }
  // if the selected array contains open, don't show the all as well
  if (
    selected.includes("OPEN") ||
    selected.includes("PENDING") ||
    selected.includes("CLOSED")
  ) {
    return [
      { label: "OPEN", value: "OPEN" },
      { label: "PENDING", value: "PENDING" },
      { label: "CLOSED", value: "CLOSED" },
    ];
  }
  return [
    { label: "ALL", value: "ALL" },
    { label: "OPEN", value: "OPEN" },
    { label: "PENDING", value: "PENDING" },
    { label: "CLOSED", value: "CLOSED" },
  ];
};

const parseReportData = (item: any) => ({
  ...item,
  sum_insured: numeral(item.sum_insured).format("0,0.00"),
  fac_sum_insured: numeral(item.fac_sum_insured).format("0,0.00"),
  fac_premium: numeral(item.fac_premium).format("0,0.00"),
  brokerage_amount: numeral(item.brokerage_amount).format("0,0.00"),
  amount_due: numeral(item.amount_due).format("0,0.00"),
  hasEndorsement: item.offer_endorsment_id ? "Yes" : "No",
  period: `${moment(item.period_of_insurance_from).format("DD/MM/YYYY")} - ${moment(item.period_of_insurance_to).format("DD/MM/YYYY")}`,
});

const getDataByType = (type: string) => {
  return ["REINSURER", "RETROCEDENT"].includes(type)
    ? reinsurerTable.map((col) => ({
        ...col,
        header: col.name,
        key: col.accessor,
      }))
    : insurerTable.map((col) => ({
        ...col,
        header: col.name,
        key: col.accessor,
      }));
};
